import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListDemandRunsInput, ListSupplierPurchaseTotalsInput } from "@nexxt/contracts/procurement";
import { purchaseOrderQueries } from "@nexxt/procurement-ui";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the demand (pattern.md §10, ADR-143): the runs, one run with the POs it created, and the POs counted
// by supplier. The keys sit under the POs': a run creates POs, and a write on a PO changes what a run and the totals
// show of it.

const procurement = api.procurement;
const runs = procurement["demand-runs"];

type RunsPage = InferResponseType<typeof runs.$get, 200>;
export type DemandRun = RunsPage["items"][number];
export type DemandRunDetail = InferResponseType<(typeof runs)[":demandRunId"]["$get"], 200>;
type TotalsPage = InferResponseType<(typeof procurement)["supplier-purchase-totals"]["$get"], 200>;
export type SupplierPurchaseTotal = TotalsPage["items"][number];

/** The filters of the runs (`listDemandRunsInput`), without the paging. */
export type DemandRunFilters = Pick<ListDemandRunsInput, "season">;
/** The filters of the totals (`listSupplierPurchaseTotalsInput`), without the paging. */
export type SupplierPurchaseTotalFilters = Pick<ListSupplierPurchaseTotalsInput, "season" | "supplier" | "status">;

const RUNS_PAGE_SIZE = 25;
const TOTALS_PAGE_SIZE = 50;

const keys = {
  all: [...purchaseOrderQueries.all, "demand"] as const,
  runs: [...purchaseOrderQueries.all, "demand", "runs"] as const,
};

export const demandQueries = {
  ...keys,

  runs: (filters: DemandRunFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.runs, "list", filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          runs.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(RUNS_PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  run: (demandRunId: string) =>
    queryOptions({
      queryKey: [...keys.runs, "detail", demandRunId],
      queryFn: ({ signal }) => unwrap(runs[":demandRunId"].$get({ param: { demandRunId } }, { init: { signal } })),
    }),

  supplierTotals: (filters: SupplierPurchaseTotalFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "supplier-totals", filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          procurement["supplier-purchase-totals"].$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(TOTALS_PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),
};
